import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';

const FOCUS_AREA_STATUS_KEY_PENDING = 'pending';
const FOCUS_AREA_STATUS_KEY_IN_PROGRESS = 'in_progress';
const FOCUS_AREA_STATUS_KEY_PAUSED = 'paused';
const FOCUS_AREA_STATUS_KEY_COMPLETED = 'completed';

const FOCUS_AREA_HEALTH_KEY_ON_TRACK = 'on_track';
const FOCUS_AREA_HEALTH_KEY_AT_RISK = 'at_risk';
const FOCUS_AREA_HEALTH_KEY_OFF_TRACK = 'off_track';

const FOCUS_AREA_STATUS_LABEL_PENDING = 'Pending';
const FOCUS_AREA_STATUS_LABEL_IN_PROGRESS = 'In progress';
const FOCUS_AREA_STATUS_LABEL_PAUSED = 'Paused';
const FOCUS_AREA_STATUS_LABEL_COMPLETED = 'Completed';

const FOCUS_AREA_HEALTH_LABEL_ON_TRACK = 'On track';
const FOCUS_AREA_HEALTH_LABEL_AT_RISK = 'At risk';
const FOCUS_AREA_HEALTH_LABEL_OFF_TRACK = 'Off track';

export const FOCUS_AREA_STATUS_HEALTH_UI_SEPARATION_FEATURE_GATE =
	'mercury_web_status_health_ui_separation';

export type FocusAreaStatusKey =
	| typeof FOCUS_AREA_STATUS_KEY_PENDING
	| typeof FOCUS_AREA_STATUS_KEY_IN_PROGRESS
	| typeof FOCUS_AREA_STATUS_KEY_PAUSED
	| typeof FOCUS_AREA_STATUS_KEY_COMPLETED;

export type FocusAreaHealthKey =
	| typeof FOCUS_AREA_HEALTH_KEY_ON_TRACK
	| typeof FOCUS_AREA_HEALTH_KEY_AT_RISK
	| typeof FOCUS_AREA_HEALTH_KEY_OFF_TRACK;

export type FocusAreaStatusLabel =
	| typeof FOCUS_AREA_STATUS_LABEL_PENDING
	| typeof FOCUS_AREA_STATUS_LABEL_IN_PROGRESS
	| typeof FOCUS_AREA_STATUS_LABEL_PAUSED
	| typeof FOCUS_AREA_STATUS_LABEL_COMPLETED;

export type FocusAreaHealthLabel =
	| typeof FOCUS_AREA_HEALTH_LABEL_ON_TRACK
	| typeof FOCUS_AREA_HEALTH_LABEL_AT_RISK
	| typeof FOCUS_AREA_HEALTH_LABEL_OFF_TRACK;

export const FOCUS_AREA_STATUS_KEYS: Record<string, FocusAreaStatusKey> = {
	PENDING: FOCUS_AREA_STATUS_KEY_PENDING,
	IN_PROGRESS: FOCUS_AREA_STATUS_KEY_IN_PROGRESS,
	PAUSED: FOCUS_AREA_STATUS_KEY_PAUSED,
	COMPLETED: FOCUS_AREA_STATUS_KEY_COMPLETED,
} as const;

export const FOCUS_AREA_HEALTH_KEYS: Record<string, FocusAreaHealthKey> = {
	ON_TRACK: FOCUS_AREA_HEALTH_KEY_ON_TRACK,
	AT_RISK: FOCUS_AREA_HEALTH_KEY_AT_RISK,
	OFF_TRACK: FOCUS_AREA_HEALTH_KEY_OFF_TRACK,
} as const;

export const FOCUS_AREA_STATUS_LABELS: Record<string, FocusAreaStatusLabel> = {
	PENDING: FOCUS_AREA_STATUS_LABEL_PENDING,
	IN_PROGRESS: FOCUS_AREA_STATUS_LABEL_IN_PROGRESS,
	PAUSED: FOCUS_AREA_STATUS_LABEL_PAUSED,
	COMPLETED: FOCUS_AREA_STATUS_LABEL_COMPLETED,
} as const;

export const FOCUS_AREA_HEALTH_LABELS: Record<string, FocusAreaHealthLabel> = {
	ON_TRACK: FOCUS_AREA_HEALTH_LABEL_ON_TRACK,
	AT_RISK: FOCUS_AREA_HEALTH_LABEL_AT_RISK,
	OFF_TRACK: FOCUS_AREA_HEALTH_LABEL_OFF_TRACK,
} as const;

export const FOCUS_AREA_STATUS_KEYS_LIST: readonly FocusAreaStatusKey[] = [
	FOCUS_AREA_STATUS_KEY_PENDING,
	FOCUS_AREA_STATUS_KEY_IN_PROGRESS,
	FOCUS_AREA_STATUS_KEY_PAUSED,
	FOCUS_AREA_STATUS_KEY_COMPLETED,
] as const;

export const FOCUS_AREA_HEALTH_KEYS_LIST: readonly FocusAreaHealthKey[] = [
	FOCUS_AREA_HEALTH_KEY_ON_TRACK,
	FOCUS_AREA_HEALTH_KEY_AT_RISK,
	FOCUS_AREA_HEALTH_KEY_OFF_TRACK,
] as const;

export const FOCUS_AREA_EMPTY_HEALTH_ICON_COLOR: NewCoreIconProps['color'] =
	token('color.icon.accent.gray');

export type FocusAreaDerivedStatusKey = FocusAreaStatusKey | FocusAreaHealthKey;

export type FocusAreaDerivedStatusLabel = FocusAreaStatusLabel | FocusAreaHealthLabel;

export const FOCUS_AREA_STATUS_KEY_TO_LABEL_MAP: {
	readonly pending: 'Pending';
	readonly in_progress: 'In progress';
	readonly paused: 'Paused';
	readonly completed: 'Completed';
} = {
	[FOCUS_AREA_STATUS_KEY_PENDING]: FOCUS_AREA_STATUS_LABEL_PENDING,
	[FOCUS_AREA_STATUS_KEY_IN_PROGRESS]: FOCUS_AREA_STATUS_LABEL_IN_PROGRESS,
	[FOCUS_AREA_STATUS_KEY_PAUSED]: FOCUS_AREA_STATUS_LABEL_PAUSED,
	[FOCUS_AREA_STATUS_KEY_COMPLETED]: FOCUS_AREA_STATUS_LABEL_COMPLETED,
} as const satisfies Record<FocusAreaStatusKey, FocusAreaStatusLabel>;

export const FOCUS_AREA_HEALTH_KEY_TO_LABEL_MAP: {
	readonly on_track: 'On track';
	readonly at_risk: 'At risk';
	readonly off_track: 'Off track';
} = {
	[FOCUS_AREA_HEALTH_KEY_ON_TRACK]: FOCUS_AREA_HEALTH_LABEL_ON_TRACK,
	[FOCUS_AREA_HEALTH_KEY_AT_RISK]: FOCUS_AREA_HEALTH_LABEL_AT_RISK,
	[FOCUS_AREA_HEALTH_KEY_OFF_TRACK]: FOCUS_AREA_HEALTH_LABEL_OFF_TRACK,
} as const satisfies Record<FocusAreaHealthKey, FocusAreaHealthLabel>;

export const FOCUS_AREA_DERIVED_STATUS_KEY_TO_LABEL_MAP: {
	readonly on_track: 'On track';
	readonly at_risk: 'At risk';
	readonly off_track: 'Off track';
	readonly pending: 'Pending';
	readonly in_progress: 'In progress';
	readonly paused: 'Paused';
	readonly completed: 'Completed';
} = {
	...FOCUS_AREA_STATUS_KEY_TO_LABEL_MAP,
	...FOCUS_AREA_HEALTH_KEY_TO_LABEL_MAP,
} as const satisfies Record<FocusAreaDerivedStatusKey, FocusAreaDerivedStatusLabel>;

export const FOCUS_AREA_DERIVED_STATUS_KEY_TO_COLOR_MAP: {
	readonly pending: 'var(--ds-icon-accent-gray)';
	readonly in_progress: 'var(--ds-icon-accent-gray)';
	readonly on_track: 'var(--ds-icon-accent-lime)';
	readonly at_risk: 'var(--ds-icon-warning)';
	readonly off_track: 'var(--ds-icon-accent-red)';
	readonly paused: 'var(--ds-icon-accent-gray)';
	readonly completed: 'var(--ds-icon-accent-gray)';
} = {
	pending: token('color.icon.accent.gray'),
	in_progress: token('color.icon.accent.gray'),
	on_track: token('color.icon.accent.lime'),
	at_risk: token('color.icon.warning'),
	off_track: token('color.icon.accent.red'),
	paused: token('color.icon.accent.gray'),
	completed: token('color.icon.accent.gray'),
} as const satisfies Record<FocusAreaDerivedStatusKey, NewCoreIconProps['color']>;
